import { useState, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from 'components/Context/AuthContext';
import '../../styles/TailRiskGlobal.css';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

const cardStyle = {
  position: 'relative',
  width: '100%',
  maxWidth: '440px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '6px',
  padding: '2.5rem 2.5rem 2rem',
  backdropFilter: 'blur(8px)',
};

const fieldLabelStyle = {
  display: 'block',
  fontFamily: 'var(--font-cond)',
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.55)',
  marginBottom: '0.45rem',
};

const btnSubmitStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  background: 'var(--gold)',
  color: '#0a1118',
  fontFamily: 'var(--font-cond)',
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  padding: '0.85rem 1.5rem',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginTop: '0.4rem',
};

function PasswordInput({ id, label, value, onChange, autoComplete }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <label htmlFor={id} style={fieldLabelStyle}>{label}</label>
      <input
        id={id}
        type="password"
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="••••••••"
        autoComplete={autoComplete || 'current-password'}
        required
        style={{
          width: '100%',
          background: focused ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${focused ? 'rgba(201,168,76,0.55)' : 'rgba(255,255,255,0.14)'}`,
          borderRadius: '4px',
          color: '#ffffff',
          fontFamily: 'var(--font-body)',
          fontSize: '0.9rem',
          padding: '0.72rem 0.9rem',
          outline: 'none',
        }}
      />
    </div>
  );
}

export default function ResetPasswordAfterLogin() {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token');

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword === oldPassword) {
      setError('New password must be different from your current password.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await axios.post(
        `${apiUrl}/change_password`,
        { oldPassword, newPassword },
        { params: { token } }
      );
      setDone(true);
      // Log out and redirect to login after a brief pause
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Change Password — Tail Risk Global</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Barlow:wght@300;400;500;600&family=Barlow+Condensed:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="trg-page" style={{ minHeight: '100vh' }}>

        {/* Nav */}
        <nav>
          <div className="nav-inner">
            <a href="/" className="logo" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <svg width="26" height="19" viewBox="0 0 32 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.45 3.78L24.71 9.04L26.6 7.15L19.45 0L14.81 4.64L28.04 17.87L27.16 18.75L13.96 5.54L0 19.5L1.89 21.39L13.96 9.33L27.16 22.53L31.82 17.87L18.6 4.64L19.45 3.78Z" fill="#C9A84C" />
              </svg>
              <span style={{ fontFamily: 'var(--font-cond)', fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#fff' }}>
                Tail Risk <span style={{ color: '#d4b06a' }}>Global</span>
              </span>
            </a>
            <button
              onClick={() => navigate(-1)}
              style={{ fontFamily: 'var(--font-cond)', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M10 7H2M5 3L1 7l4 4" /></svg>
              Back
            </button>
          </div>
        </nav>

        {/* Page */}
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 1.5rem 3rem', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(201,168,76,0.05) 0%, transparent 70%)', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />

          <div style={cardStyle}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)', opacity: 0.5, borderRadius: '6px 6px 0 0' }} />

            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '1.75rem' }}>
              <svg width="20" height="14" viewBox="0 0 32 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.45 3.78L24.71 9.04L26.6 7.15L19.45 0L14.81 4.64L28.04 17.87L27.16 18.75L13.96 5.54L0 19.5L1.89 21.39L13.96 9.33L27.16 22.53L31.82 17.87L18.6 4.64L19.45 3.78Z" fill="#C9A84C" />
              </svg>
              <div>
                <div style={{ fontFamily: 'var(--font-cond)', fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#fff' }}>
                  Tail Risk <span style={{ color: '#d4b06a' }}>Global</span>
                </div>
                <div style={{ fontFamily: 'var(--font-cond)', fontSize: '0.62rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#7a8ea6', marginTop: '2px' }}>
                  Account Settings
                </div>
              </div>
            </div>

            {!done ? (
              <>
                <div style={{ width: '28px', height: '2px', background: '#C9A84C', opacity: 0.5, marginBottom: '1.2rem' }} />
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.65rem', fontWeight: 600, lineHeight: 1.2, color: '#fff', marginBottom: '0.6rem' }}>
                  Change your<br />password
                </h1>
                <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: '1.75rem' }}>
                  Enter your current password to confirm your identity, then choose a new one.
                </p>

                <form onSubmit={handleSubmit} noValidate>
                  <PasswordInput
                    id="cp-old"
                    label="Current password"
                    value={oldPassword}
                    onChange={(e) => { setOldPassword(e.target.value); setError(''); }}
                    autoComplete="current-password"
                  />

                  {/* Divider between old and new */}
                  <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.06)', margin: '0.4rem 0 1.1rem' }} />

                  <PasswordInput
                    id="cp-new"
                    label="New password"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    autoComplete="new-password"
                  />
                  <PasswordInput
                    id="cp-confirm"
                    label="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    autoComplete="new-password"
                  />

                  {error && (
                    <p style={{ fontSize: '0.82rem', color: 'rgba(255,100,100,0.9)', marginBottom: '0.9rem' }}>{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    style={{ ...btnSubmitStyle, opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                  >
                    {loading ? 'Updating...' : 'Update password'}
                    {!loading && <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 7h10M8 3l4 4-4 4" /></svg>}
                  </button>
                </form>

                <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.08)', margin: '1.5rem 0 1.1rem' }} />
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', textAlign: 'center', lineHeight: 1.7 }}>
                  Need help?{' '}
                  <a href="mailto:info@tailriskglobal.com" style={{ color: '#d4b06a', textDecoration: 'none' }}>info@tailriskglobal.com</a>
                </div>
              </>
            ) : (
              /* Success state */
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '56px', height: '56px', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.4rem' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <div style={{ width: '28px', height: '2px', background: '#C9A84C', opacity: 0.5, margin: '0 auto 1.2rem' }} />
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '0.65rem' }}>
                  Password updated
                </div>
                <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.75 }}>
                  Your password has been changed successfully.<br />
                  You'll be signed out and redirected to login.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
